import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckoutForm } from '../components/CheckoutForm.js';
import { BRAND } from '../branding.js';
import { PoweredBy } from '../components/PoweredBy.js';

interface Product {
  slug: string;
  name: string;
  description: string;
  priceRub: number;
  priceIls: number;
  merchant?: string;
  allpayInstallmentsEnabled: boolean;
  allpayMaxInstallments?: number;
  subscriptionEnabled?: boolean;
  soldOut?: boolean;
  isActive: boolean;
}

const FX_FROM_ILS = { EUR: 0.245, USD: 0.265 };

const MOBILE_QUERY = '(max-width: 820px)';

// Метаданные применённого промокода. Сумма скидки и финальная цена
// НЕ хранятся — они вычисляются в useMemo на лету по текущей валюте
// (это убирает flash «без скидки → со скидкой» при смене валюты).
interface PromoMeta {
  code: string;
  discountType: 'percent' | 'fixed';
  percentValue: number | null;
  fixedIls: number | null;
  fixedRub: number | null;
}

export function Standalone(): JSX.Element {
  const { slug = '' } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [currency, setCurrency] = useState<'RUB' | 'ILS'>('ILS');
  const [err, setErr] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false,
  );

  // Промокод — collapsed по умолчанию.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoErr, setPromoErr] = useState<string | null>(null);
  const [promoMeta, setPromoMeta] = useState<PromoMeta | null>(null);

  const applyPromo = async (): Promise<void> => {
    const code = promoInput.trim();
    if (!code || promoBusy) return;
    setPromoBusy(true);
    setPromoErr(null);
    try {
      const res = await fetch('/api/promo-codes/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, slug, currency }),
      });
      const data = (await res.json()) as {
        valid: boolean;
        code?: string;
        discountType?: 'percent' | 'fixed';
        percentValue?: number | null;
        fixedIls?: number | null;
        fixedRub?: number | null;
        errorMessage?: string;
      };
      if (!data.valid || !data.discountType) {
        setPromoErr(data.errorMessage ?? 'Промокод не действует');
        setPromoMeta(null);
        return;
      }
      setPromoMeta({
        code: data.code ?? code,
        discountType: data.discountType,
        percentValue: data.percentValue ?? null,
        fixedIls: data.fixedIls ?? null,
        fixedRub: data.fixedRub ?? null,
      });
      setPromoErr(null);
    } catch {
      setPromoErr('Не удалось проверить промокод');
    } finally {
      setPromoBusy(false);
    }
  };

  const removePromo = (): void => {
    setPromoMeta(null);
    setPromoErr(null);
    setPromoInput('');
    setPromoOpen(false);
  };

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (): void => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    fetch(`/api/product/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`http_${r.status}`))))
      .then((p: Product) => {
        setProduct(p);
        if (p.priceIls <= 0 && p.priceRub > 0) setCurrency('RUB');
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  // useMemo обязан вызываться на каждом рендере (Rules of Hooks) — ДО early return.
  // Внутри обрабатываем случай когда product ещё не загружен.
  const promo = useMemo(() => {
    if (!product) return { active: false, discount: 0, final: 0 };
    const priceMinor = currency === 'RUB' ? product.priceRub : product.priceIls;
    if (!promoMeta) return { active: false, discount: 0, final: priceMinor };
    if (promoMeta.discountType === 'percent' && promoMeta.percentValue != null) {
      const d = Math.min(Math.round((priceMinor * promoMeta.percentValue) / 100), priceMinor);
      return { active: true, discount: d, final: priceMinor - d };
    }
    if (promoMeta.discountType === 'fixed') {
      const fixed = currency === 'ILS' ? promoMeta.fixedIls : promoMeta.fixedRub;
      if (fixed && fixed > 0) {
        const d = Math.min(fixed, priceMinor);
        return { active: true, discount: d, final: priceMinor - d };
      }
    }
    return { active: false, discount: 0, final: priceMinor };
  }, [promoMeta, product, currency]);

  if (notFound) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--bg)',
          padding: 20,
        }}
      >
        <div className="card" style={{ padding: 28 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Продукт не найден</h1>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--bg)',
          color: 'var(--fg-subtle)',
        }}
      >
        Загрузка…
      </div>
    );
  }

  const sym = currency === 'ILS' ? '₪' : '₽';
  const priceMinor = currency === 'RUB' ? product.priceRub : product.priceIls;
  const promoActive = promo.active;
  const major = promo.final / 100;
  const origMajor = priceMinor / 100;
  const ilsMajor = product.priceIls / 100;
  const fmtCur = (n: number): string =>
    n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const merchantName = (product.merchant || BRAND.name).trim();
  const brandLetter = merchantName.charAt(0).toUpperCase() || 'D';

  // Промокод-блок: общий и для десктопа (левая инфо-колонка) и для мобилы
  // (под формой). Состояние/обработчики живут в одном scope, поэтому просто
  // выносим JSX и реюзаем — без передачи пропсов.
  const promoBlock = (
    <div>
      {!promoOpen && !promoMeta && (
        <button
          type="button"
          onClick={() => setPromoOpen(true)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: 'var(--accent)',
            fontSize: 13,
            cursor: 'pointer',
            textDecoration: 'underline dotted',
            textUnderlineOffset: 3,
          }}
        >
          У меня есть промокод
        </button>
      )}
      {promoOpen && !promoMeta && (
        <div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--fg-muted)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              marginBottom: 8,
            }}
          >
            Промокод
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={promoInput}
              onChange={(e) =>
                setPromoInput(e.target.value.replace(/\s+/g, '').toUpperCase())
              }
              placeholder="EARLY30"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void applyPromo();
                }
              }}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '10px 12px',
                border: '1px solid var(--hairline)',
                borderRadius: 'var(--radius)',
                fontSize: 14,
                fontFamily: 'var(--font-mono)',
                textTransform: 'uppercase',
                outline: 'none',
                background: 'var(--bg)',
                color: 'var(--fg)',
              }}
            />
            <button
              type="button"
              onClick={() => void applyPromo()}
              disabled={!promoInput.trim() || promoBusy}
              style={{
                padding: '10px 16px',
                border: '1px solid var(--hairline)',
                background: 'var(--bg)',
                color: 'var(--fg)',
                borderRadius: 'var(--radius)',
                fontSize: 14,
                fontWeight: 600,
                cursor: promoInput.trim() && !promoBusy ? 'pointer' : 'default',
                opacity: promoInput.trim() && !promoBusy ? 1 : 0.5,
              }}
            >
              {promoBusy ? '…' : 'Применить'}
            </button>
          </div>
          {promoErr && (
            <div style={{ marginTop: 6, fontSize: 13, color: 'var(--danger-fg)' }}>
              {promoErr}
            </div>
          )}
        </div>
      )}
      {promoMeta && (
        <div
          style={{
            padding: '10px 12px',
            border: '1px solid var(--hairline)',
            background: promoActive ? 'var(--accent-subtle-bg)' : 'var(--bg-subtle)',
            borderRadius: 'var(--radius)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            opacity: promoActive ? 1 : 0.7,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: promoActive ? 'var(--accent-subtle-fg)' : 'var(--fg-muted)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {promoMeta.code}
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>
              {promoActive
                ? `Скидка −${sym} ${fmtCur(promo.discount / 100)}`
                : `Не действует для ${currency === 'ILS' ? 'ILS' : 'RUB'} — переключите валюту`}
            </div>
          </div>
          <button
            type="button"
            onClick={removePromo}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: 'var(--fg-muted)',
              fontSize: 12,
              cursor: 'pointer',
              textDecoration: 'underline dotted',
              textUnderlineOffset: 3,
            }}
          >
            Убрать
          </button>
        </div>
      )}
    </div>
  );

  // Subscription products show «в месяц» after the price (both currencies). The
  // checkout itself is unchanged — for ₪ the AllPay enrollment makes it actually
  // recurring; ₽ is billed monthly on our side.
  const perMonth = product.subscriptionEnabled ? (
    <span style={{ fontSize: 22, fontWeight: 400, color: 'var(--fg-subtle)', letterSpacing: 0 }}>
      в месяц
    </span>
  ) : null;

  if (isMobile) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px 16px 32px',
          gap: 16,
        }}
      >
        <div style={{ width: '100%', maxWidth: 460 }}>
          <CheckoutForm
            product={product}
            mode="card"
            currency={currency}
            onCurrencyChange={setCurrency}
            onError={setErr}
            promoCode={promoActive ? promoMeta?.code : undefined}
            discountedAmount={promoActive ? promo.final : undefined}
            promoSlot={promoBlock}
          />
          {err && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 12px',
                background: 'var(--danger-subtle-bg)',
                color: 'var(--danger-fg)',
                borderRadius: 'var(--radius)',
                fontSize: 13,
              }}
            >
              Ошибка: {err}
            </div>
          )}
          <div
            style={{
              marginTop: 20,
              fontSize: 12,
              color: 'var(--fg-dim)',
              textAlign: 'center',
            }}
          >
            <PoweredBy />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        className="stripe-split"
        style={{
          width: '100%',
          maxWidth: 1120,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
        }}
      >
      <div
        style={{
          padding: '48px 48px 48px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
          borderRight: '1px solid var(--hairline)',
          background: 'var(--bg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: 'var(--accent-subtle-bg)',
              color: 'var(--accent-subtle-fg)',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            {brandLetter}
          </div>
          <div
            style={{
              fontSize: 12,
              letterSpacing: 0.6,
              fontWeight: 600,
              color: 'var(--fg-muted)',
              textTransform: 'uppercase',
            }}
          >
            <bdi>{merchantName}</bdi>
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 15, color: 'var(--fg-muted)', marginBottom: 10 }}>
            Оплатить «<bdi>{product.name}</bdi>»
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            {promoActive && (
              <div
                style={{
                  fontSize: 18,
                  color: 'var(--fg-subtle)',
                  textDecoration: 'line-through',
                  fontVariantNumeric: 'tabular-nums',
                  marginRight: 8,
                }}
              >
                {sym} {fmtCur(origMajor)}
              </div>
            )}
            <div
              style={{
                fontSize: 56,
                fontWeight: 700,
                letterSpacing: -1.8,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {sym} {fmtCur(major)}
            </div>
            {perMonth}
          </div>

          {product.priceIls > 0 && (
            <div
              style={{
                marginTop: 10,
                fontSize: 13,
                color: 'var(--fg-subtle)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              ≈ € {fmtCur(ilsMajor * FX_FROM_ILS.EUR)} / $ {fmtCur(ilsMajor * FX_FROM_ILS.USD)}
            </div>
          )}

          {product.priceRub > 0 && product.priceIls > 0 && (
            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setCurrency(currency === 'ILS' ? 'RUB' : 'ILS')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  color: 'var(--accent)',
                  fontSize: 13,
                  cursor: 'pointer',
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: 3,
                }}
              >
                {currency === 'ILS' ? 'Оплатить в рублях' : 'Оплатить в любой валюте'}
              </button>
            </div>
          )}

          {product.description && (
            <div
              style={{
                marginTop: 28,
                fontSize: 14,
                color: 'var(--fg-muted)',
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                // Каждая строка — в своём направлении: абзац на иврите справа налево.
                unicodeBidi: 'plaintext',
              }}
            >
              {product.description}
            </div>
          )}

          {/* Промокод — после описания */}
          <div style={{ marginTop: 22 }}>{promoBlock}</div>
        </div>

        <div
          style={{
            marginTop: 'auto',
            paddingTop: 24,
            display: 'flex',
            gap: 14,
            fontSize: 12,
            color: 'var(--fg-dim)',
          }}
        >
          <span>
            <PoweredBy />
          </span>
        </div>
      </div>

      <div
        style={{
          padding: '48px 40px 48px 48px',
          display: 'flex',
          alignItems: 'flex-start',
          background: 'var(--bg-subtle)',
        }}
      >
        <div style={{ width: '100%' }}>
          <CheckoutForm
            product={product}
            mode="split"
            currency={currency}
            onCurrencyChange={setCurrency}
            onError={setErr}
            promoCode={promoActive ? promoMeta?.code : undefined}
            discountedAmount={promoActive ? promo.final : undefined}
          />
          {err && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 12px',
                background: 'var(--danger-subtle-bg)',
                color: 'var(--danger-fg)',
                borderRadius: 'var(--radius)',
                fontSize: 13,
              }}
            >
              Ошибка: {err}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
